import React from 'react';
import Badge from '../components/common/Badge';
import { FirearmSpecificConstants } from '../constants/FirearmSpecificConstants';

/**
 * Renders caliber text with a red badge if:
 * - The caliber is "4.5 MM" or "5.5 MM" (exact match)
 * - AND the firearm is an Air Rifle or Air Pistol
 * 
 * @param caliberText - The caliber text (from caliber?.title, firearmCalibre?.title, or calibreOther)
 * @param firearmSpecificId - The firearm specific ID to check if it's an air rifle/pistol
 * @returns React element with either a red badge or plain text
 */
export const renderCaliberBadge = (
    caliberText: string | undefined | null,
    firearmSpecificId: string | undefined | null
): React.ReactElement => {
    if (!caliberText) {
        return <span>-</span>;
    }

    // Normalize IDs to lowercase for case-insensitive comparison (per repo guidelines)
    const normalizedFirearmSpecificId = firearmSpecificId?.toLowerCase();
    const isAirRifleOrPistol =
        normalizedFirearmSpecificId === FirearmSpecificConstants.AIR_RIFLE.toLowerCase() ||
        normalizedFirearmSpecificId === FirearmSpecificConstants.AIR_PISTOL.toLowerCase();

    // Check if caliber is 4.5 MM or 5.5 MM (exact match)
    const isAirCaliberMatch = caliberText === '4.5mm' || caliberText === '5.5mm';

    // Show red badge if both conditions are met
    if (isAirRifleOrPistol && isAirCaliberMatch) {
        return <Badge label={caliberText} variant="error" />;
    }

    // Otherwise show plain text
    return <span>{caliberText}</span>;
};

/**
 * Gets the firearm make title with fallback to "Other" field
 * 
 * @param makeTitle - The standard make title from firearmMake?.title
 * @param makeOther - The custom make from firearmMakeOther field
 * @returns The make title to display
 */
export const getFirearmMake = (
    makeTitle: string | undefined | null,
    makeOther: string | undefined | null
): string => {
    return makeOther || makeTitle || 'Unknown';
};
